import { useEffect, useState } from 'react';

import { Button } from 'react-bootstrap';
import { useIntl } from 'react-intl';

import { useAppSelector } from 'hooks';
import { SectionTitle } from 'components/UI';
import { useAxios } from 'configs/requests';
import { Loading } from 'components/Fetch';
import { isPatientArchived } from 'store/patient/reducer';

import Description from './Description';

interface IDescriptionsProps {
  descriptions: ICarePlanDescription[];
  care_plan_id?: string;
  getCarePlan: any;
}

const Descriptions = ({ descriptions, care_plan_id, getCarePlan }: IDescriptionsProps) => {
  const intl = useIntl();
  const [_descriptions, setDescriptions] = useState<ICarePlanDescription[]>(descriptions);
  const patient = useAppSelector(({ patient }) => patient?.data);
  const _isPatientArchived = useAppSelector(isPatientArchived);
  const [{ loading }, postEditCarePlan] = useAxios(
    {
      url: `/clinician/Patient/${patient?.uid}/CarePlan`,
      method: 'POST'
    },
    { manual: true }
  );

  const addEmptyDescription = () => {
    if (!_descriptions.find((x) => x.edit_status === 'new')) {
      setDescriptions([
        ..._descriptions,
        {
          care_plan_id,
          description: '',
          edit_status: 'new'
        }
      ]);
    }
  };

  const handleEditDescription = (descriptionEdited: any) => {
    if (!descriptionEdited) {
      /** clean empty goal Badge */
      setDescriptions(_descriptions.filter((descripition) => descripition.description_id));
      return;
    }
    /** else handle new, deleted, updated, with basic edit_status */
    const newDescriptionWithNormalStatus = { ...descriptionEdited, edit_status: 'unchanged' };
    if (descriptionEdited.edit_status === 'new') {
      setDescriptions([
        ..._descriptions.filter((description) => description.description_id),
        newDescriptionWithNormalStatus
      ]);
    } else if (descriptionEdited.edit_status === 'deleted') {
      setDescriptions(
        _descriptions.filter(
          (description) => description.description_id !== descriptionEdited.description_id
        )
      );
    } else if (descriptionEdited.edit_status === 'updated') {
      setDescriptions(
        _descriptions.map((description) => {
          if (description.description_id === descriptionEdited.description_id) {
            return newDescriptionWithNormalStatus;
          } else {
            return description;
          }
        })
      );
    }
    getCarePlan();
  };

  useEffect(() => {
    setDescriptions(descriptions || []);
  }, [descriptions]);
  return (
    <div className="mb-5">
      <div className="d-flex justify-content-between align-items-center">
        <SectionTitle
          dataTestId="careplan-descriptions-title"
          label={intl.formatMessage({ id: 'descriptionsKey' })}
        />
        {loading && <Loading />}
        <Button
          size="sm"
          variant="link"
          onClick={addEmptyDescription}
          data-test="careplan-descriptions-btn"
          disabled={_isPatientArchived}
        >
          + {intl.formatMessage({ id: 'descriptionKey' })}
        </Button>
      </div>
      {_descriptions.length > 0 ? (
        _descriptions.map((description, index) => (
          <Description
            dataTestId={`careplan-descriptions-${index}`}
            key={description.description_id || Math.random()}
            description={description}
            handleEditDescription={handleEditDescription}
            postEditCarePlan={postEditCarePlan}
            disabled={_isPatientArchived}
          />
        ))
      ) : (
        <div
          className="bg-light text-center p-3 font-weight-bolder"
          data-test="careplan-descriptions-empty"
        >
          {intl.formatMessage({ id: 'noDescriptionsSetKey' })}
        </div>
      )}
    </div>
  );
};

export default Descriptions;
